import React, { useEffect, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';

import SectionCard from './SectionCard';
import { Fact, FactGrid } from './sectionParts';
import { AdmissionCycle } from '../../../types/admissions';

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * How long a family has to accept an offer. An unanswered offer lapses on
 * its own at the end of its last day and the seat frees up. Can be changed
 * while the cycle is open — it applies to offers made from then on; staff
 * can still extend a single offer from the applicant's Decision tab.
 */
const OfferDeadlineSection: React.FC<Props> = ({ cycle, canEdit, onSave }) => {
  const current = cycle.offerValidityDays ?? 14;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState(String(current));

  useEffect(() => { if (editing) setDays(String(current)); }, [editing, current]);

  const n = Number(days);
  const valid = Number.isInteger(n) && n >= 1 && n <= 90;

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ offerValidityDays: n });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Offer deadline"
      description="How long a family has to accept a seat. Offers not accepted in time lapse on their own and the seat frees up."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setDays(String(current))}
      saving={saving}
      canSave={valid}
    >
      {editing ? (
        <TextField
          size="small"
          label="Offer valid for"
          value={days}
          onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
          error={!valid}
          helperText={valid ? 'Applies to offers made from now on.' : 'Between 1 and 90 days'}
          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
          inputProps={{ inputMode: 'numeric' }}
          sx={{ maxWidth: 240 }}
        />
      ) : (
        <FactGrid columns={2}>
          <Fact label="Offer valid for">{current} day{current === 1 ? '' : 's'}</Fact>
          <Fact label="After that">Lapses automatically</Fact>
        </FactGrid>
      )}
    </SectionCard>
  );
};

export default OfferDeadlineSection;
